import { useGetList } from "ra-core";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import { EmptyState, GraphError, GraphLoading } from "./GraphStatus";

interface SavedViewRow {
  id: string;
  label: string;
  href: string;
  primary_type?: string | null;
  possible_duplicate?: boolean;
}

/**
 * One saved view on its own page (W12 U1), opened from the sidebar: every
 * result, with its type and the possible-duplicate flag.
 */
export function GraphSavedViewPage() {
  const { id } = useParams();
  const { data, isPending, error } = useGetList<{
    id: string;
    name: string;
    results: SavedViewRow[];
  }>("saved_views", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "name", order: "ASC" },
  });
  if (isPending) return <GraphLoading />;
  if (error) return <GraphError error={error} noun="saved view" />;
  const view = (data ?? []).find((v) => v.id === id);
  if (!view) return <GraphError error={null} noun="saved view" />;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{view.name}</h1>
        <span className="text-sm text-muted-foreground">
          {view.results.length}{" "}
          {view.results.length === 1 ? "result" : "results"}
        </span>
      </div>
      <Card className="gap-0 py-0">
        {view.results.length === 0 ? (
          <EmptyState title="Nothing here yet.">
            Records appear here once they match this view.
          </EmptyState>
        ) : (
          <ul className="divide-y">
            {view.results.map((row) => (
              <li key={row.id}>
                <Link
                  to={row.href}
                  className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-muted/50"
                >
                  <span className="font-medium">{row.label}</span>
                  {row.primary_type ? (
                    <Badge variant="secondary" className="font-normal">
                      {row.primary_type.replace(/_/g, " ")}
                    </Badge>
                  ) : null}
                  {row.possible_duplicate ? (
                    <Badge variant="outline" className="font-normal">
                      possible duplicate
                    </Badge>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
